import React, { useState, useEffect } from 'react';
import {
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as ExpiredIcon,
  Warning as WarningIcon,
  PersonAdd as AssignIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { userAPI, paymentAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';
import api from '../../services/api';

const UserPackageManager = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    subscriptionStatus: 'inactive' // Changed default to 'inactive' to show users without packages first
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [duration, setDuration] = useState(30);
  const [amount, setAmount] = useState(0);
  const [modifyMode, setModifyMode] = useState(false);
  const [existingSubscription, setExistingSubscription] = useState(null);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchPackages();
  }, [page, rowsPerPage, filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getUsers({
        page: page + 1,
        limit: rowsPerPage,
        search: filters.search
      });
      
      // Enrich users with subscription status
      const usersWithSubscriptions = await Promise.all(
        response.data.data.map(async (user) => {
          const subscription = await getUserSubscription(user.id);
          return {
            ...user,
            subscription
          };
        })
      );
      
      // Apply subscription status filter
      let filteredUsers = usersWithSubscriptions;
      if (filters.subscriptionStatus === 'active') {
        filteredUsers = usersWithSubscriptions.filter(user => 
          user.subscription && user.subscription.status === 'active'
        );
      } else if (filters.subscriptionStatus === 'inactive') {
        filteredUsers = usersWithSubscriptions.filter(user => 
          !user.subscription || user.subscription.status !== 'active'
        );
      }
      
      setUsers(filteredUsers);
      setTotal(filteredUsers.length);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserSubscription = async (userId) => {
    try {
      const response = await api.get(`/users/${userId}/subscription`);
      return response.data.data;
    } catch (error) {
      return null;
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await paymentAPI.getPackages();
      setPackages(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch packages');
    }
  };

  const handleOpenDialog = async (user) => {
    setSelectedUser(user);
    setModifyMode(false);
    setExistingSubscription(null);
    setSelectedPackage('');
    setDuration(30);
    setAmount(0);
    
    // Check if user has active subscription
    try {
      const response = await api.get(`/users/${user.id}/subscription`);
      if (response.data.data && response.data.data.status === 'active') {
        setModifyMode(true);
        setExistingSubscription(response.data.data);
        setSelectedPackage(response.data.data.package_id);
        const pkg = packages.find(p => p.id == response.data.data.package_id);
        if (pkg) {
          setDuration(pkg.duration_days);
          setAmount(pkg.price);
        }
      }
    } catch (error) {
      // No active subscription
    }
    
    setDialogOpen(true);
  };

  const handlePackageChange = (packageId) => {
    setSelectedPackage(packageId);
    const selectedPkg = packages.find(p => p.id == packageId);
    if (selectedPkg) {
      setDuration(selectedPkg.duration_days);
      setAmount(selectedPkg.price);
    }
  };

  const handleAssignPackage = async () => {
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }

    setAssigning(true);
    try {
      const payload = {
        user_id: selectedUser.id,
        package_id: selectedPackage,
        duration_days: duration,
        amount: amount,
        payment_method: 'other',
        action: modifyMode ? 'modify' : 'assign'
      };

      const response = await api.post('/payments/admin-assign-package', payload);
      
      if (response.data.success) {
        toast.success(modifyMode ? 'Package modified successfully!' : 'Package assigned successfully!');
        setDialogOpen(false);
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign package');
      console.error('Assignment error:', error);
    } finally {
      setAssigning(false);
    }
  };

  const getSubscriptionStatusChip = (user) => {
    if (user.subscription && user.subscription.status === 'active') {
      const daysLeft = moment(user.subscription.expiry_date).diff(moment(), 'days');
      if (daysLeft <= 7) {
        return (
          <Chip
            icon={<WarningIcon />}
            label={`Expires in ${daysLeft} days`}
            color="warning"
            size="small"
          />
        );
      }
      return (
        <Chip
          icon={<ActiveIcon />}
          label={`Active until ${moment(user.subscription.expiry_date).format('YYYY-MM-DD')}`}
          color="success"
          size="small"
        />
      );
    }
    return (
      <Chip
        icon={<ExpiredIcon />}
        label="No Active Subscription"
        color="error"
        size="small"
      />
    );
  };

  const getPackageName = (packageId) => {
    const pkg = packages.find(p => p.id == packageId);
    return pkg ? pkg.name : 'N/A';
  };

  const handleFilterChange = (event, newValue) => {
    if (newValue !== null) {
      setFilters({ ...filters, subscriptionStatus: newValue });
      setPage(0);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          User Package Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #c62828 0%, #1565c0 100%)',
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Total Users</Typography>
          <Typography variant="h3">{users.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Without Package</Typography>
              <Typography variant="h3">
                {users.filter(u => !u.subscription || u.subscription.status !== 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Active Packages</Typography>
              <Typography variant="h3">
                {users.filter(u => u.subscription && u.subscription.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Expiring Soon</Typography>
              <Typography variant="h3">
                {users.filter(u => {
                  if (!u.subscription) return false;
                  const daysLeft = moment(u.subscription.expiry_date).diff(moment(), 'days');
                  return daysLeft <= 7 && daysLeft > 0;
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search User"
              placeholder="Name or Phone"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ToggleButtonGroup
              value={filters.subscriptionStatus}
              exclusive
              onChange={handleFilterChange}
              aria-label="subscription status"
              fullWidth
            >
              <ToggleButton value="inactive" aria-label="without package">
                <ExpiredIcon sx={{ mr: 1 }} /> Without Package
              </ToggleButton>
              <ToggleButton value="active" aria-label="with package">
                <ActiveIcon sx={{ mr: 1 }} /> With Package
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setFilters({ search: '', subscriptionStatus: 'inactive' });
                setPage(0);
              }}
              sx={{ height: 56 }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>User</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Current Package</TableCell>
                <TableCell>Subscription Status</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography color="textSecondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={user.profile_photo_url} sx={{ bgcolor: '#e53935' }}>
                          {user.first_name?.charAt(0) || user.phone?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.first_name || ''} {user.last_name || ''}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {user.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.phone}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {user.gender || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.subscription && user.subscription.status === 'active' ? (
                        <Chip 
                          label={getPackageName(user.subscription.package_id)} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="No Package" size="small" color="error" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {getSubscriptionStatusChip(user)}
                    </TableCell>
                    <TableCell>
                      {user.subscription && user.subscription.expiry_date ? 
                        moment(user.subscription.expiry_date).format('YYYY-MM-DD') : 
                        'N/A'
                      }
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AssignIcon />}
                        onClick={() => handleOpenDialog(user)}
                        sx={{
                          background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #c62828 0%, #1565c0 100%)',
                          },
                          textTransform: 'none',
                          minWidth: 120
                        }}
                      >
                        {user.subscription && user.subscription.status === 'active' ? 'Modify Package' : 'Assign Package'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Assign Package Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', 
          color: '#fff'
        }}>
          {modifyMode ? 'Modify Package' : 'Assign Package'} - {selectedUser?.first_name} {selectedUser?.last_name}
        </DialogTitle>
        <DialogContent>
          {modifyMode && existingSubscription && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              User currently has <strong>{getPackageName(existingSubscription.package_id)}</strong> package
              expiring on {moment(existingSubscription.expiry_date).format('YYYY-MM-DD')}
            </Alert>
          )}
          
          {!modifyMode && (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              Assign a package to this user. Payment method will be recorded as "Admin Pay".
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Package</InputLabel>
                <Select
                  value={selectedPackage}
                  label="Select Package"
                  onChange={(e) => handlePackageChange(e.target.value)}
                >
                  {packages.map(pkg => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      <Box>
                        <Typography variant="body1">{pkg.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          ₨ {pkg.price} - {pkg.duration_days} days
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Duration (days)"
                type="number"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value))}
                disabled={!selectedPackage}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount (NPR)"
                type="number"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value))}
                disabled={!selectedPackage}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Method"
                value="Admin Pay"
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            
            {modifyMode && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Modifying the package will replace the current subscription immediately.
                  The old subscription will be cancelled and a new one will start.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignPackage} 
            variant="contained"
            disabled={!selectedPackage || assigning}
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #c62828 0%, #1565c0 100%)',
              }
            }}
          >
            {assigning ? <CircularProgress size={24} /> : (modifyMode ? 'Modify Package' : 'Assign Package')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserPackageManager;